import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Chip,
  Paper,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  SearchOutlined,
  GridViewOutlined,
  ViewListOutlined
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchProducts } from '../store/slices/productSlice';
import { addItemLocally } from '../store/slices/cartSlice';
import ProductCard from '../components/ProductCard';

// Styled components
const ProductsContainer = styled(Container)(({ theme }) => ({
  maxWidth: '1256px !important',
  padding: '0 16px',
  
  [theme.breakpoints.up('md')]: {
    padding: '0 24px',
  },
}));

const FilterBar = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: '32px',
  boxShadow: '0 2px 8px rgba(63, 71, 77, 0.08)',
  border: '1px solid #F1F3F4',
}));

const ProductGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: '20px',
  gridTemplateColumns: 'repeat(2, 1fr)',
  
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
  },
  
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
  
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(5, 1fr)',
  },
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  borderRadius: 20,
  fontWeight: 500,
  border: '1px solid #D3D8DE',
  backgroundColor: '#FFFFFF',
  color: '#6B7280',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  
  '&:hover': {
    borderColor: '#35C5F0',
    color: '#35C5F0',
  },
  
  '&.active': {
    backgroundColor: '#35C5F0',
    borderColor: '#35C5F0',
    color: '#FFFFFF',
  },
}));


const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1') - 1);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'popular');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { products, totalPages, isLoading } = useSelector((state: RootState) => state.product);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlCategory = searchParams.get('category') || '';
    const urlPage = parseInt(searchParams.get('page') || '1') - 1;

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlCategory !== category) setCategory(urlCategory);
    if (urlPage !== page) setPage(urlPage);

    const params: any = { page: urlPage, size: 12 };
    if (urlSearch) params.search = urlSearch;
    if (urlCategory) params.categoryId = getCategoryId(urlCategory);

    dispatch(fetchProducts(params));
  }, [dispatch, searchParams]);

  const getCategoryId = (categorySlug: string): number | undefined => {
    const categoryMap: { [key: string]: number } = {
      'living-room': 1,
      'bedroom': 2,
      'dining-room': 3,
      'office': 4,
      'kitchen': 5,
      'bathroom': 6,
      'outdoor': 7,
    };
    return categoryMap[categorySlug];
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    updateSearchParams({ search, category, page: 1, sort: sortBy });
  };

  const handleCategoryChange = (newCategory: string) => {
    setCategory(newCategory);
    setPage(0);
    updateSearchParams({ search, category: newCategory, page: 1, sort: sortBy });
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    setPage(0);
    updateSearchParams({ search, category, page: 1, sort: newSort });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    const newPage = value - 1;
    setPage(newPage);
    updateSearchParams({ search, category, page: value, sort: sortBy });
  };

  const updateSearchParams = (params: { search: string; category: string; page: number; sort: string }) => {
    const newParams = new URLSearchParams();
    if (params.search) newParams.set('search', params.search);
    if (params.category) newParams.set('category', params.category);
    if (params.sort && params.sort !== 'popular') newParams.set('sort', params.sort);
    if (params.page > 1) newParams.set('page', params.page.toString());
    setSearchParams(newParams);
  };

  const handleAddToCart = (product: any) => {
    dispatch(addItemLocally({ product, quantity: 1 }));
  };

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'living-room', label: 'Living Room' },
    { value: 'bedroom', label: 'Bedroom' },
    { value: 'dining-room', label: 'Dining Room' },
    { value: 'office', label: 'Office' },
    { value: 'outdoor', label: 'Outdoor' },
  ];

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' },
  ];

  return (
    <Box sx={{ backgroundColor: '#FAFBFC', minHeight: '100vh', py: 4 }}>
      <ProductsContainer>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              color: '#1F2937',
              mb: 1,
            }}
          >
            Discover Products
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#6B7280',
              fontSize: '16px',
            }}
          >
            Find the perfect items for your home and lifestyle
          </Typography>
        </Box>

        {/* Search & Filter Bar */}
        <FilterBar>
          <Stack spacing={3}>
            {/* Search Bar */}
            <Box>
              <form onSubmit={handleSearch}>
                <TextField
                  fullWidth
                  placeholder="Search for products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <SearchOutlined sx={{ color: '#9CA3AF', mr: 1 }} />,
                    sx: {
                      borderRadius: '12px',
                      backgroundColor: '#F5F7FA',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#EAEDEF',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 0 0 3px rgba(53, 197, 240, 0.1)',
                      },
                    },
                  }}
                />
              </form>
            </Box>

            {/* Category Filter Chips */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#374151', 
                  fontWeight: 600, 
                  mb: 1.5 
                }}
              >
                Categories
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {categories.map((cat) => (
                  <FilterChip
                    key={cat.value}
                    label={cat.label}
                    onClick={() => handleCategoryChange(cat.value)}
                    className={category === cat.value ? 'active' : ''}
                  />
                ))}
              </Stack>
            </Box>

            {/* Sort & View Options */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  {products.length > 0 && `${products.length} products found`}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D3D8DE',
                      },
                      '&:hover fieldset': {
                        borderColor: '#35C5F0',
                      },
                    }}
                  >
                    {sortOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                  <Button
                    size="small"
                    variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('grid')}
                    sx={{ minWidth: 40, p: 1 }}
                  >
                    <GridViewOutlined fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    variant={viewMode === 'list' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('list')}
                    sx={{ minWidth: 40, p: 1 }}
                  >
                    <ViewListOutlined fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Stack>
        </FilterBar>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={48} sx={{ color: '#35C5F0' }} />
          </Box>
        )}

        {/* Products Grid/List */}
        {!isLoading && (
          <>
            {products.length > 0 ? (
              <ProductGrid>
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onClick={(product) => navigate(`/products/${product.slug}`)}
                  />
                ))}
              </ProductGrid>
            ) : (
              <Paper 
                sx={{ 
                  textAlign: 'center', 
                  py: 8, 
                  px: 4, 
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                }}
              >
                <Box sx={{ maxWidth: 400, margin: '0 auto' }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#1F2937', 
                      fontWeight: 600, 
                      mb: 2 
                    }}
                  >
                    No products found
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#6B7280', 
                      mb: 3 
                    }}
                  >
                    Try adjusting your search criteria or browse different categories
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearch('');
                      setCategory('');
                      handleCategoryChange('');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear all filters
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '8px',
                      fontWeight: 500,
                      
                      '&.Mui-selected': {
                        backgroundColor: '#35C5F0',
                        color: '#FFFFFF',
                        
                        '&:hover': {
                          backgroundColor: '#1AADD8',
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </ProductsContainer>
    </Box>
  );
};

export default ProductsPage;